import FaIcon from './shared/icon';
import { useP21UserContext } from '../context/p21-user-context';
import { groups } from '../constants/p21/role';
import { GridContainer, GridItem } from './shared/ui-extension';
import { Button, Link, Menu, MenuItem, Stack, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { colors } from '../shared/styles';

const MenuButton = ({ link }) => {
  const theme = useTheme();
  const router = useRouter();

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const onMenuClicked = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getTitle = (title, icon) => {
    return (
      <Stack direction='row'>
        {icon && <FaIcon icon={icon} />}
        {title}
      </Stack>
    );
  };

  return link.subMenu ? (
    <>
      <Button
        sx={{
          color: theme.palette.common.white,
          '&:hover': {
            backgroundColor: colors.lightWhite,
          },
          padding: '8px 16px',
        }}
        onClick={onMenuClicked}
      >
        {getTitle(link.name, link.icon)}
      </Button>
      <Menu
        key={link.key}
        title={getTitle(link.name, link.icon)}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: colors.blue,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[3],
              border: `1px solid ${colors.lightWhite}`,
            },
          },
        }}
      >
        {link.subMenu.map((subLink) => {
          return (
            <MenuItem
              key={subLink.key}
              sx={{
                padding: '10px 24px',
                '&:hover': {
                  backgroundColor: colors.lightWhite,
                },
              }}
              onClick={() => {
                handleClose();
                router.push(subLink.link);
              }}
              title={subLink.name}
            >
              <Link
                href={subLink.link}
                onClick={(e) => {
                  e.preventDefault();
                }}
                sx={{ color: theme.palette.common.white }}
              >
                {subLink.name}
              </Link>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  ) : (
    <Button
      onClick={() => {
        router.push(link.link);
      }}
    >
      {getTitle(link.name, link.icon)}
    </Button>
  );
};

export const HeaderMenu = () => {
  const { permissions, p21User } = useP21UserContext();

  const { shippingQueueVisibleRoles, worldEaseAdminRoles } = groups;

  const renderMenu = () => {
    const menuLinks = [
      {
        key: 'customers',
        name: 'Accounts',
        icon: 'group',
        subMenu: [
          {
            key: 'order-entry',
            name: 'Order Entry',
            link: '/order-entry',
          },
          {
            key: 'credit-apps',
            name: 'Credit Applications',
            link: '/credit-applications-v2',
          },
          {
            key: 'credit-health',
            name: 'Credit Health',
            link: '/customer-credit-health',
          },

          {
            key: 'customer-page',
            name: 'Account',
            link: '/customer',
          },
          ...(permissions.hasPermissionToViewAccountTracker
            ? [
                {
                  key: 'account-tracker',
                  name: 'Account Tracker',
                  link: '/account-tracker',
                },
              ]
            : []),
          {
            key: 'error-logging',
            name: 'Error Entry',
            link: '/error-logging',
          },
        ],
      },
      {
        key: 'm-bot-main',
        name: 'M-Bot',
        icon: 'local_shipping',
        subMenu: [
          { key: 'm-bot', name: 'M-Bot', link: '/m-bot-v2' },
          {
            key: 'enter-pro',
            name: 'Manage PRO Numbers',
            link: '/manage-pro-numbers',
          },
          ...(shippingQueueVisibleRoles.includes(p21User?.role) || !p21User.role
            ? [
                {
                  key: 'shipping-queue',
                  name: 'Shipping Queue',
                  link: '/shipping-queue',
                },
              ]
            : []),
          ...(worldEaseAdminRoles.includes(p21User?.role) || !p21User.role
            ? [
                {
                  key: 'world-ease-shipment-management',
                  name: 'World Ease Shipment Management',
                  link: '/world-ease-shipment-management',
                },
              ]
            : []),
        ],
      },
      {
        key: 'marketing',
        name: 'Marketing',
        icon: 'view_module',
        subMenu: [
          {
            key: 'manage-design-order',
            name: 'Manage Design Order',
            link: '/marketing/design-order/manage',
          },
          {
            key: 'create-design-order',
            name: 'Create Design Order',
            link: '/marketing/design-order/create',
          },
        ],
      },
    ];

    if (permissions.isSupplyChain || permissions.isWarehouse) {
      menuLinks.push({
        key: 'supply-chain',
        name: 'Supply Chain',
        icon: 'inventory',
        subMenu: [
          ...((permissions.isSupplyChain && [
            {
              key: 'inventory-details',
              name: 'Inventory Allocation',
              link: '/inventory-management',
            },
            {
              key: 'manage-pos',
              name: 'Manage POs',
              link: '/manage-pos',
            },
            {
              key: 'manage-transfers',
              name: 'Manage Transfers',
              link: '/manage-transfers',
            },
            {
              key: 'manage-trucks/manage-trucks',
              name: 'Manage Trucks',
              link: '/manage-trucks',
            },

            {
              key: 'item/item-standard-cost',
              name: 'Item Standard Cost',
              link: '/item-standard-cost',
            },
            {
              key: 'create-edit-truck',
              name: 'Create/Edit Truck',
              link: '/create-edit-truck',
            },
            {
              key: 'receive-truck',
              name: 'Receive Truck',
              link: '/receive-truck',
            },
          ]) ||
            []),
          {
            key: 'create-new-item',
            name: 'Create/Edit Item',
            link: '/create-new-item',
          },
        ],
      });
    }

    menuLinks.push({
      key: 'product',
      name: 'Product',
      icon: 'local_offer',
      subMenu: [
        {
          key: 'item-search',
          name: 'Item Search',
          link: '/item-search',
        },
        {
          key: 'item-details',
          name: 'Item Details',
          link: '/item',
        },
        {
          key: 'sourcing-catalog',
          name: 'Sourcing Catalog',
          link: '/sourcing-catalog',
        },
        {
          key: 'vendor-supplier-search',
          name: 'Vendor Supplier Search',
          link: '/vendor-supplier-search',
        },
        {
          key: 'vendor-supplier-details',
          name: 'Vendor Supplier Details',
          link: '/vendor-supplier-details',
        },
      ],
    });

    return menuLinks.map((link, i) => (
      <GridItem key={i}>
        <MenuButton link={link} />
      </GridItem>
    ));
  };

  return <GridContainer>{renderMenu()}</GridContainer>;
};
